import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PushNotification from './PushNotification';
import pushNotificationService from '../services/pushNotificationService';
import eventoService from '../services/eventoService';
import notificacionService from '../services/notificacionService';

const PushNotificationContainer = () => {
    const [activeNotifications, setActiveNotifications] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        // Suscribirse a nuevas notificaciones
        const unsubscribe = pushNotificationService.subscribe((notificacion) => {
            setActiveNotifications(prev => [...prev, notificacion]);
        });

        // Cargar notificaciones pendientes al iniciar
        cargarNotificacionesPendientes();

        // Polling para nuevas notificaciones cada 30 segundos
        const interval = setInterval(() => {
            cargarNotificacionesPendientes();
        }, 60000);

        return () => {
            unsubscribe();
            clearInterval(interval);
        };
    }, []);

    const cargarNotificacionesPendientes = async () => {
        try {
            // Consultar tanto pendientes como las últimas enviadas (para InApp recientes)
            const [pendientesRes, ultimasRes] = await Promise.all([
                notificacionService.getPendientes(),
                notificacionService.getUltimas()
            ]);
            
            const rawPendientes = pendientesRes?.data ?? pendientesRes;
            const rawUltimas = ultimasRes?.data ?? ultimasRes;
            const pendientes = Array.isArray(rawPendientes) ? rawPendientes : [];
            const ultimas = Array.isArray(rawUltimas) ? rawUltimas : [];

            if (!Array.isArray(rawPendientes)) console.warn('⚠️ PushContainer: pendientes no es array:', rawPendientes);
            if (!Array.isArray(rawUltimas)) console.warn('⚠️ PushContainer: ultimas no es array:', rawUltimas);

            // Filtrar notificaciones InApp que no hemos mostrado
            // Incluir tanto pendientes como recientes (últimas 5, creadas en los últimos 30 segundos)
            const ahora = new Date();
            const hace30Segundos = new Date(ahora.getTime() - 30000); // 30 segundos atrás
            
            setActiveNotifications(current => {
                const existingIds = new Set(current.map(n => n.id));
                
                // Notificaciones InApp pendientes
                const inAppPendientes = pendientes.filter(n => 
                    n.canal === 'INAPP' && 
                    !n.enviado &&
                    n.id &&
                    !existingIds.has(n.id)
                );
                
                // Notificaciones InApp recientes (marcadas como enviadas pero creadas recientemente)
                const inAppRecientes = ultimas.filter(n => 
                    n.canal === 'INAPP' &&
                    n.id &&
                    !existingIds.has(n.id) &&
                    n.fechaProgramada && 
                    new Date(n.fechaProgramada) >= hace30Segundos
                );

                // Combinar y mostrar
                const todasInApp = [...inAppPendientes, ...inAppRecientes];
                todasInApp.forEach(notif => {
                    pushNotificationService.addNotification(notif);
                });

                return current;
            });
        } catch (err) {
            console.error('Error al cargar notificaciones pendientes:', err);
        }
    };
    
    // Función para consultar inmediatamente después de crear un evento
    const consultarNotificacionesInmediatas = async () => {
        // Esperar 1 segundo para que el backend procese la notificación
        setTimeout(() => {
            cargarNotificacionesPendientes();
        }, 1000);
    };
    
    // Exponer la función para que otros componentes puedan usarla
    useEffect(() => {
        // Agregar la función al servicio para que sea accesible globalmente
        window.consultarNotificacionesInmediatas = consultarNotificacionesInmediatas;
        
        return () => {
            delete window.consultarNotificacionesInmediatas;
        };
    }, []);

    const handleClose = async (notificacionId) => {
        // Remover de la vista activa
        setActiveNotifications(prev => 
            prev.filter(n => n.id !== notificacionId)
        );
        
        // Marcar como enviada en el backend para que aparezca en la campanita
        // (no se elimina, solo se marca como "enviada" para que vaya a la campanita)
        try {
            await notificacionService.marcarComoLeida(notificacionId);
        } catch (err) {
            console.error('Error al marcar notificación como leída:', err);
        }
    };

    const handleViewDetail = (evento) => {
        setSelectedEvent(evento);
    };

    const closeEventModal = () => {
        setSelectedEvent(null);
    };

    return (
        <>
            {/* Renderizar notificaciones push */}
            <div className="push-notification-container">
                {activeNotifications.map((notif, index) => (
                    <div
                        key={notif.id || `notif-${index}`}
                        style={{
                            marginTop: index > 0 ? '10px' : '0'
                        }}
                    >
                        <PushNotification
                            notificacion={notif}
                            onClose={() => handleClose(notif.id)}
                            onViewDetail={handleViewDetail}
                        />
                    </div>
                ))}
            </div>

            {/* Modal de detalle del evento */}
            {selectedEvent && (
                <div className="event-dropdown-overlay" onClick={closeEventModal}>
                    <div className="event-dropdown" onClick={e => e.stopPropagation()}>
                        <div className="dropdown-header">
                            <h4>Detalles del Evento</h4>
                            <button className="dropdown-close-btn" onClick={closeEventModal}>×</button>
                        </div>

                        <div className="event-details">
                            <div className="event-title">
                                <h3>{selectedEvent.titulo}</h3>
                            </div>

                            <div className="event-info">
                                <div className="info-item">
                                    <span className="info-label">📅 Fecha:</span>
                                    <span className="info-value">
                                        {new Date(selectedEvent.fecha).toLocaleDateString()}
                                    </span>
                                </div>

                                <div className="info-item">
                                    <span className="info-label">🕐 Horario:</span>
                                    <span className="info-value">
                                        {selectedEvent.todoElDia
                                            ? "Todo el día"
                                            : `${selectedEvent.horaInicio || "–"} a ${selectedEvent.horaFin || "–"}`}
                                    </span>
                                </div>

                                <div className="info-item">
                                    <span className="info-label">🏷️ Tipo:</span>
                                    <span className="info-value">
                                        {typeof selectedEvent.tipoEvento === "string"
                                            ? selectedEvent.tipoEvento
                                            : selectedEvent.tipoEvento?.nombre || "Sin tipo"}
                                    </span>
                                </div>

                                {selectedEvent.idTramite && (
                                    <div className="info-item">
                                        <span className="info-label">📋 Trámite asociado:</span>
                                        <span className="info-value">ID #{selectedEvent.idTramite}</span>
                                    </div>
                                )}
                            </div>

                            {selectedEvent.descripcion && (
                                <div className="event-description mt-2">
                                    <div className="descripcion-label">📝 Descripción:</div>
                                    <div className="descripcion-text">{selectedEvent.descripcion}</div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PushNotificationContainer;

